import {
  ZonePolicy,
  CreateZonePolicyMutationVariables,
  UpdateZonePolicyMutationVariables,
  useCreateZonePolicyMutation,
  useUpdateZonePolicyMutation,
  useDeleteZonePolicyMutation,
} from '../../../../types/generated-types';
import React, { ReactElement, useState } from 'react';
import { FormValues } from '../types/zonePolicyDetailFormValues';
import { Form, Formik, FormikHelpers, useFormik } from 'formik';
import * as yup from 'yup';
import Box from '@mui/material/Box';
import { Button } from '@mui/material';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import { ZonePolicyGeneralTab } from '../components/zone-policy-detail-general-tab';
import { ZonePolicyPropertiesTab } from '../components/zone-policy-detail-properties-tab';
import { useNavigate } from 'react-router-dom';
import { useAuthenticator } from '../../../auth/AuthenticationContext';
import { Notifier } from '../../../system/services/notificationManager';
import { ActivityIndicator } from '../../shared/activityIndicator';
import { BulkUpdateStatus } from '../../bulkConfig/common/bulk-update-status';
import { VerifyZonePolicyUpdate } from '../components/verify-zone-policy-update';
import { removeTypeNames } from '../components/zone-policy-detail-general-tab';
export const SimpleZonePolicyForm = ({
  policy,
}: {
  policy: Partial<ZonePolicy>;
}): ReactElement => {
  const navigator = useNavigate();
  const auth = useAuthenticator();
  const [propagating, setPropagating] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verificationReady, setVerificationReady] = useState(false);

  const [updateZonePolicy] = useUpdateZonePolicyMutation();
  const [createZonePolicy] = useCreateZonePolicyMutation();
  const [deleteZonePolicy] = useDeleteZonePolicyMutation();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirming, setDeleteConfirming] = useState(false);

  const initialValues: FormValues = {
    _id: policy._id ?? '',
    name: policy.name ?? '',
    rules: policy.rules ?? [],
    properties: (policy.properties ?? []) as string[],
  };

  const onSubmit = (
    values: FormValues,
    _helpers: FormikHelpers<FormValues>,
  ) => {
    if (policy._id) {
      setPropagating(true);
      setVerifying(false);
      setVerificationReady(false);
      const policyUpdateFields: UpdateZonePolicyMutationVariables['policy'] = {
        name: values.name,
        rules: removeTypeNames(values.rules),
        properties: values.properties,
      };

      updateZonePolicy({
        variables: {
          id: policy._id,
          policy: policyUpdateFields,
        },
      })
        .then(() => {
          formik.setSubmitting(false);
          Notifier.success('Zone policy updated');
        })
        .catch((updateError) => {
          formik.setSubmitting(false);
          Notifier.error(updateError.message);
          setPropagating(false);
        });
    } else {
      const policyInput: CreateZonePolicyMutationVariables['policy'] = {
        name: values.name,
        rules: removeTypeNames(values.rules),
        properties: values.properties,
      };
      createZonePolicy({
        variables: { policy: policyInput },
      })
        .then(() => {
          formik.setSubmitting(false);
          Notifier.success('Zone policy created');
          navigator('/zone-policies');
        })
        .catch((createError) => {
          formik.setSubmitting(false);
          Notifier.error(createError.message);
        });
    }
  };

  const handleDeleteClick = () => setDeleteDialogOpen(true);
  const handleDeleteDialogClose = () => {
    if (!deleteConfirming) setDeleteDialogOpen(false);
  };
  const handleDeleteConfirm = () => {
    if (!policy._id) return;
    setDeleteConfirming(true);
    deleteZonePolicy({ variables: { id: policy._id } })
      .then(() => {
        Notifier.success('Zone policy deleted');
        setDeleteDialogOpen(false);
        setDeleteConfirming(false);
        navigator('/zone-policies');
      })
      .catch((err) => {
        Notifier.error(err.message);
        setDeleteConfirming(false);
      });
  };

  const validationSchema = yup.object({
    name: yup.string().required('Name is required'),
    rules: yup.array(),
    properties: yup.array(),
  });
  const isCreate = !policy._id;

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  const [value, setValue] = React.useState<number>(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const tabStyles = {
    error: {
      color: 'red',
      fontWeight: '600',
    },
    normal: {
      fontWeight: 'inherit',
    },
  };

  return auth.user ? (
    <div style={{ padding: '6px' }}>
      <h2>
        {policy._id ? 'Edit' : 'Create'} Zone Policy
        {formik.values.name ? `: ${formik.values.name}` : ''}
      </h2>
      <Formik initialValues={initialValues} onSubmit={onSubmit}>
        <Form
          style={{ width: '100%', fontSize: '16px' }}
          onSubmit={formik.handleSubmit}
        >
          {propagating ? (
            <div>
              <ActivityIndicator activityInProgress={formik.isSubmitting} />
              <BulkUpdateStatus
                title="Setpoint Profile Propagation Status"
                initiator={policy}
                completeCallback={() => {
                  setPropagating(false);
                  formik.resetForm({ values: formik.values });
                }}
                actions={[
                  {
                    label: 'Return to Zone Policy List',
                    handler: () => navigator('/zone-policies'),
                  },
                  {
                    label: 'Return to editing',
                    handler: () => {
                      setPropagating(false);
                      formik.resetForm({ values: formik.values });
                    },
                  },
                ]}
              />
            </div>
          ) : (
            <div>
              {verifying ? (
                <div
                  style={{
                    marginBottom: '6px',
                    paddingBottom: '6px',
                    borderBottom: '1px solid lightgray',
                  }}
                >
                  <VerifyZonePolicyUpdate
                    values={formik.values}
                    initialValues={initialValues}
                    dataReadyCallback={setVerificationReady}
                  />
                </div>
              ) : (
                <div>
                  <Box
                    sx={{
                      borderBottom: 1,
                      borderColor: 'divider',
                      marginBottom: '18px',
                    }}
                  >
                    <Tabs
                      value={value}
                      onChange={handleTabChange}
                      aria-label="View or Edit Zone Policy"
                      TabIndicatorProps={{
                        style: { color: '#000' },
                      }}
                      indicatorColor="secondary"
                      textColor="secondary"
                    >
                      <Tab
                        label="General"
                        style={
                          formik.errors.name || formik.errors.rules
                            ? tabStyles.error
                            : tabStyles.normal
                        }
                        id="zone-policy-general-tab"
                      />
                      <Tab
                        label="Properties"
                        style={
                          formik.errors.properties
                            ? tabStyles.error
                            : tabStyles.normal
                        }
                        id="zone-policy-properties-tab"
                      />
                    </Tabs>
                  </Box>
                  <ActivityIndicator activityInProgress={formik.isSubmitting} />
                  {value === 0 ? (
                    <ZonePolicyGeneralTab
                      currentUser={auth.user}
                      formik={formik}
                      onChange={(e) => {
                        formik.setFieldTouched((e.target as any).id, true);
                        formik.handleChange(e);
                      }}
                    />
                  ) : (
                    <ZonePolicyPropertiesTab formik={formik} />
                  )}
                </div>
              )}
              <br />
              <div
                style={{
                  marginTop: '10px',
                  marginLeft: '10px',
                  padding: '24px',
                }}
              >
                {isCreate ? (
                  <Button
                    disabled={!formik.isValid || !formik.dirty}
                    type="submit"
                    variant="contained"
                    color="secondary"
                    sx={{ color: '#fff', fontSize: '12px' }}
                  >
                    Create Zone Policy
                  </Button>
                ) : verifying && verificationReady ? (
                  <Button
                    disabled={!formik.isValid || !formik.dirty}
                    type="submit"
                    variant="contained"
                    color="secondary"
                    sx={{ color: '#fff', fontSize: '12px' }}
                  >
                    Commit Changes
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="secondary"
                    sx={{ color: '#fff', fontSize: '12px' }}
                    disabled={!formik.isValid || !formik.dirty}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setVerifying(true);
                    }}
                  >
                    Verify Changes
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="secondary"
                  sx={{ color: '#fff', marginLeft: '10px', fontSize: '12px' }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (verifying) {
                      setVerifying(false);
                      setVerificationReady(false);
                    } else {
                      navigator('/zone-policies');
                    }
                  }}
                >
                  {verifying ? 'Return To Editing' : 'Return To List'}
                </Button>
                {!isCreate && (
                  <Button
                    variant="contained"
                    color="error"
                    sx={{ marginLeft: '10px', fontSize: '12px' }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDeleteClick();
                    }}
                  >
                    Delete
                  </Button>
                )}
              </div>
              <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose}>
                <DialogTitle>Delete zone policy?</DialogTitle>
                <DialogContent>
                  <DialogContentText>
                    This will remove the zone policy from all cores and delete
                    it from the system. Zones will be disassociated from this
                    policy. This action cannot be undone.
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={handleDeleteDialogClose}
                    disabled={deleteConfirming}
                  >
                    Cancel
                  </Button>
                  <Button
                    color="error"
                    variant="contained"
                    onClick={handleDeleteConfirm}
                    disabled={deleteConfirming}
                  >
                    {deleteConfirming ? 'Deleting…' : 'Delete'}
                  </Button>
                </DialogActions>
              </Dialog>
            </div>
          )}
        </Form>
      </Formik>
    </div>
  ) : (
    <div>Unauthorized</div>
  );
};
